import { useCallback, useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getOrCreateChat, sendTextMessage } from "../utils/chat";

// Single shared implementation of "one realtime 1:1 chat thread", used by
// both the Buddies.js embedded chat panel and ChatPage.js — previously each
// page reimplemented its own find-or-create + (sometimes missing) realtime
// subscription, which is exactly the kind of duplicated messaging logic/
// duplicate-listener risk this task asks to avoid. There is still only ONE
// underlying chats/{chatId} document and ONE onSnapshot per open chat.
export function useChatThread(currentUid, otherUid) {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!currentUid || !otherUid) return undefined;

    let cancelled = false;
    let unsubscribe = null;
    setLoading(true);
    setError("");
    setMessages([]);
    setChatId(null);

    (async () => {
      try {
        const chat = await getOrCreateChat(currentUid, otherUid);
        if (cancelled) return;
        setChatId(chat.id);

        unsubscribe = onSnapshot(
          doc(db, "chats", chat.id),
          (snap) => {
            if (cancelled) return;
            setMessages(snap.exists() ? snap.data().messages || [] : []);
            setLoading(false);
          },
          (err) => {
            console.error("Chat listener error:", err);
            if (!cancelled) {
              setError("loadError");
              setLoading(false);
            }
          }
        );
      } catch (err) {
        console.error("Failed to open chat:", err);
        if (!cancelled) {
          setError("loadError");
          setLoading(false);
        }
      }
    })();

    // Always unsubscribe the previous chat's listener before starting a new
    // one (new effect run) or on unmount — this is what actually prevents
    // the "switching chats leaks listeners" failure mode.
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentUid, otherUid]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      // Guards against empty/whitespace-only sends and accidental double
      // sends (e.g. double Enter/double-tap) while a send is in flight.
      if (!trimmed || !chatId || sendingRef.current) return false;
      sendingRef.current = true;
      setSending(true);
      setError("");
      try {
        await sendTextMessage(chatId, currentUid, trimmed);
        // No optimistic local append — the onSnapshot listener above is the
        // single source of truth, so the message appears exactly once.
        return true;
      } catch (err) {
        console.error("Failed to send message:", err);
        setError("sendError");
        return false;
      } finally {
        sendingRef.current = false;
        setSending(false);
      }
    },
    [chatId, currentUid]
  );

  return { chatId, messages, loading, error, sending, sendMessage, setError };
}
